import { Experiment, ExperimentClient } from '@amplitude/experiment-js-client';
import { createContext, FC, PropsWithChildren, useContext, useEffect, useState } from 'react';
import envService from './env.service';

// Feature flags that have a boolean value
export enum FeatureFlags {
  Test = 'test',
  IsSkipAccountCreationEnabled = 'skip-email-capture-pre-checkout-grow-131_v2',
  IsRiskTrendTabEnabled = 'risk-trend-tab-enabled',
}

// Feature flags that have multiple variants as a string
export enum VariantFlags {
  PPRAVersion = 'ppra-version',
  IsPpraMembershipInterstitialEnabled = 'ppra-membership-interstitial-enabled',
}

const defaultFeatureFlags: Record<VariantFlags, string> = {
  [VariantFlags.PPRAVersion]: 'email_first',
  [VariantFlags.IsPpraMembershipInterstitialEnabled]: 'control',
};

interface FeatureFlagContextValue {
  isEnabled: (name: FeatureFlags) => boolean;
  getVariant: (name: VariantFlags) => string;
  isLoading: boolean;
}

const FeatureFlagContext = createContext<FeatureFlagContextValue | undefined>(undefined);

export const FeatureFlagProvider: FC<PropsWithChildren> = ({ children }) => {
  const [instance, setInstance] = useState<ExperimentClient>();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initializeFeatureFlags = async () => {
      try {
        setIsLoading(true);
        const experimentInstance = Experiment.initializeWithAmplitudeAnalytics(
          envService.getEnv().VITE_AMPLITUDE_API_KEY
          // TODO: Uncomment this when we debug the proxy
          // {
          //   // serverUrl: envService.getEnv().VITE_AMPLITUDE_EXP_PROXY_URL,
          //   // flagsServerUrl: envService.getEnv().VITE_AMPLITUDE_FF_PROXY_URL,
          // }
        );
        await experimentInstance.fetch();
        setInstance(experimentInstance);
      } catch (error) {
        console.error('Failed to initialize feature flags:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeFeatureFlags();
  }, []);

  const isEnabled = (name: FeatureFlags): boolean => {
    if (!instance || isLoading) return false;
    const variant = instance.variant(name);
    return variant.value === 'on';
  };

  const getVariant = (name: VariantFlags): string => {
    if (!instance || isLoading) return defaultFeatureFlags[name];
    const variant = instance.variant(name);
    return variant.value ?? defaultFeatureFlags[name];
  };

  const contextValue: FeatureFlagContextValue = {
    isEnabled,
    getVariant,
    isLoading,
  };

  return <FeatureFlagContext.Provider value={contextValue}>{children}</FeatureFlagContext.Provider>;
};

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagContext);
  if (context === undefined) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagProvider');
  }
  return context;
};

export default FeatureFlagProvider;
